import { useEffect, useState } from 'react';
import { Loader2, RefreshCw, X, Check, Undo2 } from 'lucide-react';
import supabase from '../lib/supabase';

type Payment = {
  id: string;
  amount: number;
  currency: string;
  status: string;
  payment_method: string;
  stripe_payment_intent_id: string | null;
  stripe_charge_id: string | null;
  created_at: string;
  booking_id: string | null;
  court_booking_id: string | null;
  coach_booking_id: string | null;
  session_package_purchase_id: string | null;
  bookings: { booking_reference: string } | null;
  court_bookings: { booking_reference: string } | null;
  coach_bookings: { booking_reference: string } | null;
};

type Filter = 'refundable' | 'all' | 'refunded';

export default function Refunds() {
  const [rows, setRows] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<Filter>('refundable');
  const [modal, setModal] = useState<Payment | null>(null);

  const load = async () => {
    setLoading(true);
    let q = supabase
      .from('payments')
      .select(`
        id, amount, currency, status, payment_method,
        stripe_payment_intent_id, stripe_charge_id, created_at,
        booking_id, court_booking_id, coach_booking_id, session_package_purchase_id,
        bookings(booking_reference),
        court_bookings(booking_reference),
        coach_bookings(booking_reference)
      `)
      .order('created_at', { ascending: false })
      .limit(200);
    if (filter === 'refundable') q = q.in('status', ['paid', 'partially_refunded']);
    if (filter === 'refunded') q = q.in('status', ['refunded', 'partially_refunded']);
    const { data } = await q;
    setRows((data as unknown as Payment[]) ?? []);
    setLoading(false);
  };

  useEffect(() => { load(); }, [filter]);

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl">Refunds</h1>
          <p className="text-sm text-gray-500">
            Issue full or partial refunds. Connect transfers to clubs / coaches are reversed automatically.
          </p>
        </div>
        <button onClick={load} className="h-10 px-3 rounded-lg border border-gray-300 text-sm">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="flex gap-1 p-1 bg-gray-100 rounded-lg mb-4 w-fit">
        {(['refundable', 'all', 'refunded'] as Filter[]).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`h-8 px-4 rounded-md text-sm capitalize ${
              filter === f ? 'bg-white shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="text-left px-4 py-3">Date</th>
              <th className="text-left px-4 py-3">Booking</th>
              <th className="text-left px-4 py-3">Type</th>
              <th className="text-right px-4 py-3">Amount</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-gray-500">
                  {loading ? 'Loading…' : 'No payments match this filter.'}
                </td>
              </tr>
            ) : (
              rows.map((p) => {
                const ref =
                  p.coach_bookings?.booking_reference
                  ?? p.court_bookings?.booking_reference
                  ?? p.bookings?.booking_reference
                  ?? (p.session_package_purchase_id ? `PKG-${p.session_package_purchase_id.slice(0, 8)}` : '—');
                const type =
                  p.coach_booking_id && p.court_booking_id ? 'Bundle'
                  : p.coach_booking_id ? 'Coach'
                  : p.court_booking_id ? 'Court'
                  : p.booking_id ? 'Machine'
                  : p.session_package_purchase_id ? 'Package'
                  : '—';
                const refundable = ['paid', 'partially_refunded'].includes(p.status);
                return (
                  <tr key={p.id} className="border-t border-gray-100">
                    <td className="px-4 py-3 text-gray-500">
                      {new Date(p.created_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{ref}</td>
                    <td className="px-4 py-3 text-xs uppercase text-gray-500">{type}</td>
                    <td className="px-4 py-3 text-right">
                      {p.currency} {Number(p.amount).toFixed(2)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded-full capitalize ${
                        p.status === 'paid' ? 'bg-green-100 text-green-800'
                        : p.status === 'refunded' ? 'bg-gray-100 text-gray-600'
                        : p.status === 'partially_refunded' ? 'bg-amber-100 text-amber-900'
                        : p.status === 'failed' ? 'bg-red-100 text-red-800'
                        : 'bg-amber-100 text-amber-800'
                      }`}>
                        {p.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {refundable && (
                        <button
                          onClick={() => setModal(p)}
                          className="h-8 px-3 rounded-lg border border-gray-300 hover:bg-gray-50 text-xs flex items-center gap-1 ml-auto"
                        >
                          <Undo2 className="w-3.5 h-3.5" />
                          Refund
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {modal && (
        <RefundModal
          payment={modal}
          onClose={() => setModal(null)}
          onDone={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
}

function RefundModal({
  payment, onClose, onDone,
}: { payment: Payment; onClose: () => void; onDone: () => void }) {
  // Two distinct refund flows in one modal:
  //   - 'customer'  → admin-refund — refunds the customer's card and
  //                   (optionally) reverses the Connect transfer
  //   - 'platform'  → admin-refund-application-fee — refunds AceRent's
  //                   commission to the connected account; customer
  //                   payment is untouched
  const [mode, setMode] = useState<'customer' | 'platform'>('customer');
  const [amount, setAmount] = useState(String(Number(payment.amount).toFixed(2)));
  const [reason, setReason] = useState('');
  const [reverseTransfer, setReverseTransfer] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const amt = Number(amount);
    if (!amt || amt <= 0) { setErr('Amount must be > 0'); return; }
    if (mode === 'customer' && amt > Number(payment.amount)) {
      setErr('Amount exceeds original payment'); return;
    }
    setBusy(true);
    setErr(null);

    if (mode === 'customer') {
      const { error } = await supabase.functions.invoke('admin-refund', {
        body: { paymentId: payment.id, amount: amt, reason: reason || null, reverseTransfer },
      });
      setBusy(false);
      if (error) { setErr(error.message); return; }
      await supabase.rpc('audit_log', {
        p_action: amt >= Number(payment.amount) ? 'refund_issued_full' : 'refund_issued_partial',
        p_target_type: 'payment',
        p_target_id: payment.id,
        p_metadata: { amount: amt, currency: payment.currency, reason, reverseTransfer } as any,
      });
      onDone();
      return;
    }

    // mode === 'platform': refund the application fee only.
    // amount is in AED on the form; the edge function expects fils.
    const { error } = await supabase.functions.invoke('admin-refund-application-fee', {
      body: {
        paymentId: payment.id,
        amount: Math.round(amt * 100),
        reason: reason || null,
      },
    });
    setBusy(false);
    if (error) {
      const ctx: any = (error as any).context;
      let detail: string | null = null;
      try {
        const bodyText = typeof ctx?.body === 'string' ? ctx.body : null;
        const parsed = bodyText ? JSON.parse(bodyText) : null;
        detail = parsed?.error ?? null;
      } catch { /* fall through */ }
      setErr(detail ?? (error as any).message ?? 'Application-fee refund failed.');
      return;
    }
    onDone();
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-lg max-w-md w-full p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold">Issue refund</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Original payment: {payment.currency} {Number(payment.amount).toFixed(2)}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-2 p-1 rounded-lg bg-gray-100">
            <button
              type="button"
              onClick={() => setMode('customer')}
              className={`h-9 rounded-md text-xs font-medium transition ${
                mode === 'customer' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500'
              }`}
            >
              Refund customer
            </button>
            <button
              type="button"
              onClick={() => setMode('platform')}
              className={`h-9 rounded-md text-xs font-medium transition ${
                mode === 'platform' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500'
              }`}
            >
              Refund platform fee only
            </button>
          </div>
          <p className="text-[11px] text-gray-500 -mt-1">
            {mode === 'customer'
              ? 'Refunds the customer\'s card. Optionally pulls the funds back from the club / coach.'
              : 'Refunds AceRent\'s commission to the club / coach. Customer\'s payment is untouched — use this to comp service-recovery cases.'}
          </p>

          <label className="block">
            <div className="text-xs text-gray-600 mb-1">
              Amount ({payment.currency})
              {mode === 'platform' && (
                <span className="ml-1 text-gray-400">— up to the platform commission on this payment</span>
              )}
            </div>
            <input
              type="number" step="0.01" value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
              className="w-full h-10 px-3 rounded-lg border border-gray-300 text-sm"
            />
            <div className="text-xs text-gray-400 mt-1">
              {mode === 'customer'
                ? 'Leave at full amount for a complete refund (cancels the booking).'
                : 'Stripe rejects amounts above the original commission; the function will validate.'}
            </div>
          </label>

          <label className="block">
            <div className="text-xs text-gray-600 mb-1">Reason (shown to the recipient)</div>
            <textarea
              value={reason} onChange={(e) => setReason(e.target.value)}
              rows={3} maxLength={300}
              className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm"
              placeholder="Customer requested cancellation due to travel…"
            />
          </label>

          {mode === 'customer' && (
            <label className="flex items-start gap-2 text-sm text-gray-700">
              <input
                type="checkbox" checked={reverseTransfer}
                onChange={(e) => setReverseTransfer(e.target.checked)}
                className="mt-0.5"
              />
              <span>
                Reverse Connect transfer — pull the refund back from the club / coach.
                Leave on unless you want the platform to absorb the cost.
              </span>
            </label>
          )}

          {err && <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">{err}</div>}

          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 h-10 rounded-lg border border-gray-300 text-sm">
              Cancel
            </button>
            <button type="submit" disabled={busy}
              className="flex-1 h-10 rounded-lg bg-[#2d5016] text-white text-sm flex items-center justify-center gap-1 disabled:opacity-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Issue refund
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
